import {
  assertInInjectionContext,
  EnvironmentProviders,
  inject,
  InjectionToken,
  makeEnvironmentProviders,
  Provider,
} from '@angular/core';
import { LgsConfiguration, LgsFeatures } from './lgs-configuration';

export const environment = getConfig();

export function injectLgsConfiguration(): LgsConfiguration {
  assertInInjectionContext(injectLgsConfiguration);
  return inject(APPLICATION_CONFIGURATION);
}

export function provideLgsConfiguration(): EnvironmentProviders {
  const providers: Provider[] = [
    { provide: APPLICATION_CONFIGURATION, useFactory: getLgsConfiguration },
  ];

  return makeEnvironmentProviders(providers);
}

function getConfig(): {
  apiURL: string;
  auth0: { domain: string; clientId: string; audience: string };
  university: 'undj' | 'uvt' | 'enstp' | 'staging' | 'usarh' | 'insatal' | 'insem';
  features: LgsFeatures;
} {
  switch (window.location.host) {
    case 'app.universite-ndjamena.td':
    case 'lgs.universite-ndjamena.td':
      return {
        apiURL: 'https://undj.welma-education.com/api',
        auth0: {
          domain: 'universite-ndjamena.eu.auth0.com',
          clientId: 'AewJdKGzFYxnZfd5cBy8ySoff0oX9JWU',
          audience: 'https://api.universite-ndjamena.td',
        },
        university: 'undj',
        features: {
          card: 'v1',
        },
      };
    case 'lgs.universite-virtuelle.td':
      return {
        apiURL: 'https://uvt.welma-education.app/api',
        auth0: {
          domain: 'universite-virtuelle.eu.auth0.com',
          clientId: 'CNx6RUN7bumrScAQgiDx6gR1eVXG24Ge',
          audience: 'https://api.universite-virtuelle.td',
        },
        university: 'uvt',
        features: {
          card: 'v2',
        },
      };
    case 'lgs.welma.app':
    case 'www.welma.app':
      return {
        apiURL: 'https://staging.welma-education.app/api',
        auth0: {
          domain: 'welma-education-staging.eu.auth0.com',
          clientId: 'XSHAc8sUutfdXeTkC0u30MtjUxnqfpwk',
          audience: 'https://api.welma.app',
        },
        university: 'staging',
        features: {
          card: 'v3',
        },
      };
    case 'enstp.lgs.education.welma.app':
    case 'lgs.enstp.td':
      return {
        apiURL: 'https://enstp.welma-education.app/api',
        auth0: {
          domain: 'enstp.eu.auth0.com',
          clientId: 'NPDtlN2JEvLCcyJwqXJcdDIpnMqTO9aJ',
          audience: 'https://enstp.api.education.welma.app',
        },
        university: 'enstp',
        features: {
          card: 'v3',
        },
      };
    case 'lgs.universite-sarh.td':
      return {
        apiURL: 'https://usarh.welma-education.app/api',
        auth0: {
          domain: 'usarh.eu.auth0.com',
          clientId: 'cj06pg3ZyqaQWgTe7A6vCApNaqhceHUj',
          audience: 'https://usarh.api.education.welma.app',
        },
        university: 'usarh',
        features: {
          card: 'v3',
        },
      };
    case 'insem.lgs.education.welma.app':
    case 'lgs.insem.td':
      return {
        apiURL: 'https://insem.welma-education.app/api',
        auth0: {
          domain: 'insem.eu.auth0.com',
          clientId: 'dn8CE3iyhCv28dQSmLAFIFb3cwqRjWYv',
          audience: 'https://insem.api.education.welma.app',
        },
        university: 'insem',
        features: {
          card: 'v3',
        },
      };
    case 'lgs.insatal.td':
      return {
        apiURL: 'https://insatal.welma-education.app/api',
        auth0: {
          domain: 'insatal.eu.auth0.com',
          clientId: '1SpDskCB4BhlP4fVOr6UjcNtIxigaFua',
          audience: 'https://insatal.api.education.welma.app',
        },
        university: 'insatal',
        features: {
          card: 'v3',
        },
      };
    default:
      return {
        apiURL: '/api',
        auth0: {
          domain: 'welma-education-staging.eu.auth0.com',
          clientId: 'XSHAc8sUutfdXeTkC0u30MtjUxnqfpwk',
          audience: 'https://api.welma.app',
        },
        university: 'usarh',
        features: {
          card: 'v3',
        },
      };
  }
}

const APPLICATION_CONFIGURATION = new InjectionToken<LgsConfiguration>('APPLICATION_CONFIGURATION');

const configs = {
  uvt: {
    university: {
      country: 'République du Tchad',
      name: `Université Numérique du Tchad`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/v1767698119/logo-universite-numerique-du-tchad_xmbbtg.png',
      website: 'www.unt.td',
      address: 'BP : 5711 N’Djaména - Tchad',
      city: `N'Djaména`,
      boss: {
        title: 'Le Président',
        name: 'Mahamat Charfadine Salim',
      },
      certificateSigner: {
        title: 'Le Vice-Président chargé des enseignements',
        name: 'Dr BATOUMA NARKOY',
        signature: '',
      },
      reportSigner: {
        title: 'Le Responsable de la Cellule Études, Planification et Scolarité',
        direction: 'Cellule Études, Planification et Scolarité',
      },
    },
    theme: {
      primary: '#1A78F4',
      accent: '#FBC700',
      hideName: true,
    },
  },
  undj: {
    university: {
      country: 'République du Tchad',
      name: `Université de N'Djaména`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/logo-ndjamena_z9zede.png',
      website: 'www.universite-ndjamena.td',
      address: 'B.P: 1117 / Tél. (235) 22 51 54 54',
      city: `N'Djaména`,
      boss: {
        title: 'Mahamat Saleh Daoussa Haggar',
        name: 'Le Président',
      },
      certificateSigner: {
        title: 'Le Directeur de la Scolarité et des Examens',
        name: 'Pr Amir Moungache',
        signature:
          'https://res.cloudinary.com/doknvxtth/image/upload/v1761381871/signature-directeur-undj_k2rolk.png',
      },
      reportSigner: {
        title: 'Le Chef du Service de la Scolarité et des Examens',
        direction: 'Service de la Scolarité et des Examens',
      },
    },
    theme: {
      primary: '#002569',
      accent: '#FFCE00',
      hideName: false,
    },
  },
  staging: {
    university: {
      country: 'République du Tchad',
      name: `Université Welma`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/v1708868688/logo-512x512_d1qk85.png',
      website: 'www.welma.io',
      address: '21 Ouest Foire, Dakar - Sénégal',
      city: `Dakar`,
      boss: {
        title: 'Directeur Général',
        name: 'Amadou Sall',
      },
      certificateSigner: {
        title: 'Le Directeur de la Scolarité et des Examens',
        name: 'Pr. Ahmad Thiam',
        signature: '',
      },
      reportSigner: {
        title: 'Le Chef du Service de la Scolarité et des Examens',
        direction: 'Service de la Scolarité et des Examens',
      },
    },
    theme: {
      primary: '#2251CC',
      accent: '#B620E0',
      hideName: false,
    },
  },
  enstp: {
    university: {
      country: 'République du Tchad',
      name: `École Nationale Supérieure des Travaux Publics`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/v1704708383/logo-enstp-square_y7e5f1.png',
      website: 'www.enstp.td',
      address: 'B.P: 60 / Tél. (235) 22 52 34 20',
      city: `N'Djaména`,
      boss: {
        title: 'Le Directeur Général',
        name: 'KOINA RODOUMTA',
      },
      certificateSigner: {
        title: 'Directeur des Études',
        name: 'GOUDJA ABDOULAYE MAINA',
        signature:
          'https://res.cloudinary.com/doknvxtth/image/upload/v1761137087/signature-directeur-enstp_pbymyn.png',
      },
      reportSigner: {
        title: 'Le Directeur Général',
        direction: 'Service de la Scolarité et des Examens',
      },
    },
    theme: {
      primary: '#2A3084',
      accent: '#EC752E',
      hideName: false,
    },
  },
  usarh: {
    university: {
      country: 'République du Tchad',
      name: `Université de Sarh`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/v1762520874/usarh_yzvpdg.png',
      website: 'www.uds.td',
      address: 'Route de Bangui, Sarh',
      city: `Sarh`,
      boss: {
        title: 'Le Président',
        name: 'Prof. NGARKODJE NGARASTA',
      },
      certificateSigner: {
        title: 'Le Directeur de la Scolarité et des Examens',
        name: '',
        signature: '',
      },
      reportSigner: {
        title: 'Le Chef de Service de la Scolarité et des Statistiques',
        direction: 'Service de la Scolarité et des Statistiques',
      },
    },
    theme: {
      primary: '#FFB905',
      accent: '#FEAEA0',
      hideName: false,
    },
  },
  insatal: {
    university: {
      country: 'République du Tchad',
      name: `Institut National Supérieur des Sciences Agronomiques et des Technologies Agroalimentaires de Laï`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/v1762539097/logo-insatal_n3zusi.png',
      website: 'www.insatal.td',
      address: 'Laï',
      city: `Laï`,
      boss: {
        title: 'Le Directeur Général',
        name: 'DJEKOTA CHRISTOPHE',
      },
      certificateSigner: {
        title: 'Le Directeur de la Scolarité et des Examens',
        name: 'TODO',
        signature: '',
      },
      reportSigner: {
        title: 'Le Directeur Général',
        direction: 'Service de la Scolarité et des Examens',
      },
    },
    theme: {
      primary: '#086437',
      accent: '#E3ED66',
      hideName: false,
    },
  },
  insem: {
    university: {
      country: 'République du Tchad',
      name: `Institut National Supérieur d'Elevage de Moussoro`,
      logo: 'https://res.cloudinary.com/doknvxtth/image/upload/v1762539107/logo-insem_whxojo.png',
      website: 'www.insem.td',
      address: 'Moussoro',
      city: `Moussoro`,
      boss: {
        title: 'Le Directeur Général',
        name: 'TODO',
      },
      certificateSigner: {
        title: 'Directeur des Études',
        name: 'Directeur des Études',
        signature: '',
      },
      reportSigner: {
        title: 'Le Directeur Général',
        direction: 'Service de la Scolarité et des Examens',
      },
    },
    theme: {
      primary: '#893826',
      accent: '#588526',
      hideName: false,
    },
  },
} as const satisfies Record<string, Omit<LgsConfiguration, 'features'>>;

function getLgsConfiguration(): LgsConfiguration {
  const config = configs[environment.university];
  return {
    university: config.university,
    theme: config.theme,
    features: environment.features,
  };
}
